if (!customElements.get('country-selector')) {
  const hongKongDistricts = {
    'Hong Kong Island': [
      ['Central and Western', 'Central and Western', '中西區'],
      ['Eastern', 'Eastern', '東區'],
      ['Southern', 'Southern', '南區'],
      ['Wan Chai', 'Wan Chai', '灣仔區'],
    ],
    Kowloon: [
      ['Kowloon City', 'Kowloon City', '九龍城區'],
      ['Yau Tsim Mong', 'Yau Tsim Mong', '油尖旺區'],
      ['Sham Shui Po', 'Sham Shui Po', '深水埗區'],
      ['Wong Tai Sin', 'Wong Tai Sin', '黃大仙區'],
      ['Kwun Tong', 'Kwun Tong', '觀塘區'],
    ],
    'New Territories': [
      ['Tai Po', 'Tai Po', '大埔區'],
      ['Yuen Long', 'Yuen Long', '元朗區'],
      ['Tuen Mun', 'Tuen Mun', '屯門區'],
      ['North', 'North', '北區'],
      ['Sai Kung', 'Sai Kung', '西貢區'],
      ['Sha Tin', 'Sha Tin', '沙田區'],
      ['Tsuen Wan', 'Tsuen Wan', '荃灣區'],
      ['Kwai Tsing', 'Kwai Tsing', '葵青區'],
      ['Islands', 'Islands', '離島區'],
    ],
  };

  class CountrySelector extends HTMLElement {
    constructor() {
      super();
      this.provinceElement = this.querySelector('.js-shipping-province');
      this.countryElement = this.querySelector('.js-shipping-country');
      this.provinceWrapper = this.querySelector('.js-province-container');
      this.areaElement = this.querySelector('.js-hk-area');
      this.districtElement = this.querySelector('.js-hk-district');
      this.eligibilityMessage = this.querySelector('.js-hk-delivery-eligible');
      this.useTraditionalChinese = document.documentElement.lang.toLowerCase().startsWith('zh');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      CountrySelector.selectOption(this.countryElement, this.countryElement.dataset.default);

      if (this.areaElement) {
        const hongKongOption = Array.from(this.countryElement.options).find((option) =>
          CountrySelector.isHongKong(option)
        );
        if (hongKongOption) this.countryElement.selectedIndex = hongKongOption.index;
      }

      this.updateProvinceOptions();

      if (this.provinceElement.dataset.default && this.provinceElement.options.length > 0) {
        CountrySelector.selectOption(this.provinceElement, this.provinceElement.dataset.default);
      }

      this.countryElement.addEventListener('change', this.updateProvinceOptions.bind(this));

      if (this.areaElement) {
        this.areaElement.addEventListener('change', this.updateDistrictOptions.bind(this));
        this.districtElement.addEventListener('change', this.updateDeliveryEligibility.bind(this));
      }
    }

    /**
     * Updates the province/state options based on selected country.
     */
    updateProvinceOptions() {
      const selectedCountry = this.countryElement.options[this.countryElement.selectedIndex];
      const provinceList = JSON.parse(selectedCountry.dataset.provinces);

      // Clear existing options in the province field.
      Array.from(this.provinceElement.options).forEach((option) => option.remove());

      if (provinceList && provinceList.length === 0) {
        this.provinceWrapper.hidden = true;
      } else {
        provinceList.forEach((province) => {
          const provinceOption = document.createElement('option');
          [provinceOption.value, provinceOption.innerHTML] = province;
          this.provinceElement.appendChild(provinceOption);
        });

        this.provinceWrapper.hidden = false;
      }
    }

    updateDistrictOptions() {
      const districts = hongKongDistricts[this.areaElement.value] || [];
      const placeholder = this.useTraditionalChinese ? '選擇分區' : 'Select a district';
      const districtLabelIndex = this.useTraditionalChinese ? 2 : 1;
      this.districtElement.replaceChildren(new Option(placeholder, ''));

      districts.forEach((district) =>
        this.districtElement.add(new Option(district[districtLabelIndex], district[0]))
      );

      this.districtElement.disabled = districts.length === 0;
      this.eligibilityMessage.hidden = true;

      if (this.provinceElement.options.length > 0) {
        CountrySelector.selectOption(this.provinceElement, this.areaElement.value);
      }
    }

    updateDeliveryEligibility() {
      this.eligibilityMessage.hidden = this.districtElement.value === '';
    }

    /**
     * Sets the selected option of a <select> element.
     * @param {Element} dropdown - Country or province <select> element.
     * @param {string} value - The value to be selected in the dropdown.
     */
    static selectOption(dropdown, value) {
      Array.from(dropdown.options).forEach((option, index) => {
        if (option.value === value || option.innerHTML === value) {
          dropdown.selectedIndex = index;
        }
      });
    }

    static isHongKong(option) {
      const countryName = `${option.value} ${option.textContent}`.toLowerCase();
      return countryName.includes('hong kong') || countryName.includes('香港');
    }
  }

  customElements.define('country-selector', CountrySelector);
}

if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.submitButton = this.querySelector('button');
      this.shippingForm = this.querySelector('form');
      this.shippingForm.addEventListener('submit', this.processFormSubmit.bind(this));
    }

    initialize() {
      this.countryField = this.querySelector('.js-shipping-country');
      this.provinceField = this.querySelector('.js-shipping-province');
      this.cityField = this.querySelector('.js-city-input');
      this.addressField = this.querySelector('.js-address-input');
      this.zipField = this.querySelector('.js-zip-input');
      this.ratesDisplay = this.querySelector('.js-shipping-rate');
      this.errorDisplay = this.querySelector('.js-shipping-error');
      this.isInitialized = true;
    }

    /**
     * Handles the form submission for calculating shipping rates.
     * @param {object} event - The submit event object.
     */
    async processFormSubmit(event) {
      event.preventDefault();
      if (!this.isInitialized) this.initialize();

      this.errorDisplay.hidden = true;
      this.ratesDisplay.hidden = true;
      this.submitButton.disabled = true;

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.submitButton.querySelector('.loading__spinner').classList.remove('hidden', 'spinning-complete');

      const isCartDrawer = this.dataset.context === 'cart-drawer';
      const queryParams = new URLSearchParams({
        'shipping_address[country]': this.countryField.value,
        'shipping_address[province]': isCartDrawer
          ? this.querySelector('.js-hk-area').value
          : this.provinceField.value,
        'shipping_address[city]': isCartDrawer
          ? this.querySelector('.js-hk-district').value
          : this.cityField?.value || '',
        'shipping_address[address1]': this.addressField?.value || '',
      });

      if (this.zipField.value) queryParams.set('shipping_address[zip]', this.zipField.value);

      try {
        const response = await fetch(`${window.routes.cart_url}/shipping_rates.json?${queryParams.toString()}`);
        const resultData = await response.json();

        if (response.ok) {
          this.displayRates(resultData);
        } else {
          this.displayErrors(resultData);
        }
      } catch (error) {
        console.error(error); // eslint-disable-line
      } finally {
        this.submitButton.disabled = false;
        this.submitButton.setAttribute('aria-disabled', false);
        this.submitButton.classList.remove('loading');
        this.submitButton.querySelector('.loading__spinner').classList.add('hidden', 'spinning-complete');
      }
    }

    /**
     * Displays the available shipping rates.
     * @param {object} data - The response data from the shipping API.
     */
    displayRates(data) {
      const headingElement = this.ratesDisplay.querySelector('.js-shipping-rate-heading');
      const textElement = this.ratesDisplay.querySelector('.js-shipping-rate-text');
      textElement.innerHTML = '';

      if (data.shipping_rates && data.shipping_rates.length) {
        const headingText = data.shipping_rates.length === 1 ? 'singleRate' : 'multipleRates';
        let rateOptions = '';

        data.shipping_rates.forEach((rate) => {
          const formattedPrice = theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, rate.price);
          const localizedRateName = this.localizeRateName(rate.name);
          rateOptions += `<li>${localizedRateName}: ${formattedPrice}</li>`;
        });

        headingElement.textContent = window.strings.cart.shippingCalculator[headingText];
        textElement.innerHTML = `<ul class="mt-1 t5">${rateOptions}</ul>`;
        headingElement.hidden = false;
      } else {
        headingElement.hidden = true;
        textElement.innerHTML = window.strings.cart.shippingCalculator.noRates;
      }

      this.ratesDisplay.hidden = false;
    }

    localizeRateName(rateName) {
      const localizedRateNames = window.strings.cart.shippingCalculator.rateNames;
      const rateNameMap = {
        '順豐到付 (運費由買家支付給順豐)': localizedRateNames.sfExpressFreightCollect,
        '滿 HKD $600 免運費優惠': localizedRateNames.freeDeliveryOver600,
      };

      return rateNameMap[rateName] || rateName;
    }

    /**
     * Displays errors related to shipping rate retrieval.
     * @param {object} data - The error data from the shipping API.
     */
    displayErrors(data) {
      let errorMessages = '';

      Object.keys(data).forEach((key) => {
        errorMessages += `<li>${data[key]}</li>`;
      });

      this.errorDisplay.querySelector('.js-shipping-error-content').innerHTML = `<ul class="mt-1 t5">${errorMessages}</ul>`;
      this.errorDisplay.hidden = false;
    }
  }

  customElements.define('shipping-calculator', ShippingCalculator);
}
